import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { toast } from 'sonner';
import type { UserData } from '../mockRoleData';
import type { UserRoleServiceInstance } from './useEditRoleData';

export interface UseTransferUserOptions {
  roleId?: string;
  userRoleService: UserRoleServiceInstance;
  setUsers: Dispatch<SetStateAction<UserData[]>>;
}

/** Owns the transfer-user dialog state and the transfer API flow. */
export const useTransferUser = ({ roleId, userRoleService, setUsers }: UseTransferUserOptions) => {
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [newRoleId, setNewRoleId] = useState<string>('');

  const handleTransferRole = (user: UserData) => {
    setSelectedUser(user);
    setNewRoleId('');
    setTransferDialogOpen(true);
  };

  const handleConfirmTransfer = async () => {
    if (!selectedUser || !newRoleId || !roleId) return;
    try {
      try {
        await userRoleService.transferEmployeeToRole(Number(roleId), Number(selectedUser.id), Number(newRoleId));
      } catch (transferError: unknown) {
        const is404 = (transferError instanceof Error && 'status' in transferError && (transferError as Record<string, unknown>).status === 404) || (transferError instanceof Error && (transferError.message.includes('not found') || transferError.message.includes('404')));
        if (!is404) throw transferError;
        // Atomic endpoint not deployed yet — fall back to remove + assign with rollback
        await userRoleService.removeEmployeeFromRole(Number(roleId), Number(selectedUser.id));
        try {
          await userRoleService.assignEmployeeToRole(Number(newRoleId), Number(selectedUser.id));
        } catch (assignError: unknown) {
          // Assign failed — rollback by re-adding to original role
          await userRoleService.assignEmployeeToRole(Number(roleId), Number(selectedUser.id));
          throw assignError;
        }
      }
      setUsers(prev => prev.filter(u => u.id !== selectedUser.id));
      window.dispatchEvent(new Event('rolesUpdated'));
      setTransferDialogOpen(false);
      setSelectedUser(null);
      setNewRoleId('');
    } catch (error: unknown) {
      toast.error('Transfer failed', { description: error instanceof Error ? error.message : 'Failed to transfer user' });
    }
  };

  return {
    transferDialogOpen,
    setTransferDialogOpen,
    selectedUser,
    newRoleId,
    setNewRoleId,
    handleTransferRole,
    handleConfirmTransfer,
  };
};
